"use client";

import { useState, useRef } from "react";
import { Upload, X, AlertCircle, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

interface MediaItem {
  id?: string | number;
  url: string;
  type: "image";
  is_primary: boolean;
  lang: string;
  order?: number;
}

interface AddOnMediaUploadProps {
  media: MediaItem[];
  onChange: (media: MediaItem[]) => void;
  acceptedTypes?: string[];
  language: string;
  venueId: number;
  addOnId: number;
}

export default function AddOnMediaUpload({
  media,
  onChange,
  acceptedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"],
  language,
  venueId,
  addOnId,
}: AddOnMediaUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // Get the current image for this language (only one)
  const currentImage = media.find((m) => m.lang === language);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    await uploadFile(file);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const uploadFile = async (file: File) => {
    setError(null);
    setIsUploading(true);
    setUploadingFile(file.name);

    try {
      // If there's an existing image, remove it first
      if (currentImage) {
        await removeMedia(currentImage.url, false);
      }

      // Upload to Supabase storage
      const formData = new FormData();
      formData.append("file", file);
      formData.append("bucket", "add-on-media");

      const uploadResponse = await fetch("/api/upload", {
        method: "POST",
        body: formData,
      });

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json();
        throw new Error(errorData.error || "Upload failed");
      }

      const { url } = await uploadResponse.json();

      // Save media record in database
      const mediaResponse = await fetch(
        `/api/venues/${venueId}/add-ons/${addOnId}/media`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            url,
            type: "image",
            lang: language,
            is_primary: true,
            order: 0,
          }),
        },
      );

      if (!mediaResponse.ok) {
        throw new Error("Failed to save media record");
      }

      // Fetch updated media from server to get the ID
      const refreshResponse = await fetch(
        `/api/venues/${venueId}/add-ons/${addOnId}/media`,
      );
      if (refreshResponse.ok) {
        const { data } = await refreshResponse.json();
        onChange(data || []);
      } else {
        // Fallback to local update if refresh fails
        const newMediaItem: MediaItem = {
          url,
          type: "image",
          is_primary: true,
          lang: language,
          order: 0,
        };
        // Keep other languages, replace current language
        const otherLanguageMedia = media.filter((m) => m.lang !== language);
        onChange([...otherLanguageMedia, newMediaItem]);
      }

      toast.success("Image uploaded successfully");
    } catch (err: any) {
      setError(err.message || "Failed to upload file");
      toast.error(err.message || "Failed to upload file");
    } finally {
      setIsUploading(false);
      setUploadingFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));

    if (imageFiles.length === 0) {
      setError("Please drop image files only");
      return;
    }

    // Only process the first file
    await uploadFile(imageFiles[0]);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const removeMedia = async (url: string, showToast: boolean = true) => {
    try {
      // First update local state
      const updatedMedia = media.filter((m) => m.url !== url);
      onChange(updatedMedia);

      // Then update database with all remaining media for this add-on
      const response = await fetch(
        `/api/venues/${venueId}/add-ons/${addOnId}/media`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            media_items: updatedMedia,
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to remove media");
      }

      if (showToast) {
        toast.success("Image removed successfully");
      }
    } catch (error) {
      if (showToast) {
        toast.error("Failed to remove image");
      }
      // Revert local state on error
      onChange(media);
    }
  };

  return (
    <div className="space-y-4">
      {/* Show current image or upload area */}
      {currentImage ? (
        // Image preview with visible action buttons
        <div className="space-y-3">
          <Card className="relative overflow-hidden w-80 h-80">
            <div className="w-full h-full bg-gray-100">
              <img
                src={currentImage.url}
                alt="Add-on image"
                className="w-full h-full object-cover"
              />
            </div>
          </Card>

          {/* Always visible action buttons */}
          <div className="flex gap-3">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors duration-200 disabled:opacity-50"
            >
              {isUploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              Replace image
            </button>
            <button
              onClick={() => removeMedia(currentImage.url)}
              disabled={isUploading}
              className="flex items-center gap-2 px-4 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-sm font-medium transition-colors duration-200 disabled:opacity-50"
            >
              <X className="h-4 w-4" />
              Remove
            </button>
          </div>

          {/* Format info */}
          <div className="text-xs text-gray-500 space-y-0.5">
            <p>JPG, PNG, WEBP or GIF (max 10MB)</p>
            <p>Recommended 4:3 ratio (e.g. 1200x900)</p>
          </div>
        </div>
      ) : (
        // Upload area when no image
        <Card
          className={`border-2 border-dashed p-8 text-center transition-colors cursor-pointer ${
            isDragOver
              ? "border-purple-500 bg-purple-50"
              : "border-gray-300 hover:border-gray-400"
          } ${isUploading ? "opacity-50 cursor-not-allowed" : ""}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => !isUploading && fileInputRef.current?.click()}
        >
          {isUploading ? (
            <div className="space-y-2">
              <Loader2 className="h-10 w-10 mx-auto animate-spin text-purple-600" />
              <p className="text-sm text-gray-600">
                Uploading {uploadingFile}...
              </p>
            </div>
          ) : (
            <>
              <Upload className="h-10 w-10 mx-auto mb-3 text-gray-400" />
              <p className="text-sm text-gray-600 mb-2">
                Click to upload or drag and drop
              </p>
              <p className="text-xs text-gray-500">
                JPG, PNG, WEBP or GIF (max 10MB)
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Recommended 4:3 ratio (e.g. 1200x900)
              </p>
            </>
          )}
        </Card>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedTypes.join(",")}
        onChange={handleFileSelect}
        className="hidden"
        disabled={isUploading}
      />

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-md">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}
    </div>
  );
}
